import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { ApiData, ApiResponse } from 'src/app/core/interfaces/api/api-response.interface';
import { environment } from 'src/environments/environment';
import { CasinoStatistics } from '../../interfaces/api/casino.interface';
import {
  BettingCategoryStats,
  BonusPromotionalCode,
  CasinoBetReport,
  DownloadReportRequest,
  GeneralCasionoReportByAgent,
  GroupedLoginAttemptBackOffice,
  GroupedSportsBetting,
  LoginAttemptBackOffice,
  LoginAttemptPlayer,
  PlayerReport,
  ProviderReportData,
  ReportExport,
  ReportHistoryItem,
  SportsBetting,
  VirtualBet,
} from '../../interfaces/api/report.interface';

@Injectable({
  providedIn: 'root',
})
export class ReportService {
  private apiUrl: string = `${environment.apiUrl}/report`;
  private apiReportsUrl: string = `${environment.apiReportsUrl}/report`;
  private apiDatamartUrl: string = `${environment.apiReportsUrl}/datamart`;

  constructor(
    private _httpClient: HttpClient,
    private _translate: TranslateService
  ) { }

  public findBettingReport(filter: object): Observable<ApiResponse<ApiData<SportsBetting[]>>> {
    const endpoint = `${this.apiUrl}/sports-betting`;
    const params = new HttpParams({ fromObject: { ...filter } });
    return this._httpClient.get<ApiResponse<ApiData<SportsBetting[]>>>(endpoint, { params });
  }

  public findSportsReport(
    filter: object
  ): Observable<ApiResponse<ApiData<GroupedSportsBetting[]>>> {
    const endpoint = `${this.apiUrl}/sports-betting/sports`;
    const params = new HttpParams({ fromObject: { ...filter } });
    return this._httpClient.get<ApiResponse<ApiData<GroupedSportsBetting[]>>>(endpoint, { params });
  }

  public findCategoriesReport(
    filter: object
  ): Observable<ApiResponse<ApiData<GroupedSportsBetting[]>>> {
    const endpoint = `${this.apiUrl}/sports-betting/categories`;
    const params = new HttpParams({ fromObject: { ...filter } });
    return this._httpClient.get<ApiResponse<ApiData<GroupedSportsBetting[]>>>(endpoint, { params });
  }

  public findEventsReport(filter: object): Observable<ApiResponse<ApiData<any[]>>> {
    const endpoint = `${this.apiUrl}/sports-betting/events`;
    const params = new HttpParams({ fromObject: { ...filter } });
    return this._httpClient.get<ApiResponse<ApiData<GroupedSportsBetting[]>>>(endpoint, { params });
  }

  public findMarketsReport(filter: object): Observable<ApiResponse<ApiData<any[]>>> {
    const endpoint = `${this.apiUrl}/sports-betting/markets`;
    const params = new HttpParams({ fromObject: { ...filter } });
    return this._httpClient.get<ApiResponse<ApiData<GroupedSportsBetting[]>>>(endpoint, { params });
  }

  public getReportByCasimoGames(filter: object): Observable<ApiResponse<ApiData<any[]>>> {
    const endpoint = `${this.apiReportsUrl}/casino/report-by-games`;
    const params = new HttpParams({ fromObject: { ...filter } });
    return this._httpClient.get<ApiResponse<ApiData<any[]>>>(endpoint, {
      params,
    });
  }

  public getReportByCurrency(filter: object): Observable<ApiResponse<ApiData<CasinoStatistics[]>>> {
    const params = new HttpParams({ fromObject: { ...filter } });
    const endpoint = `${this.apiReportsUrl}/casino/report-by-currencies`;
    return this._httpClient.get<ApiResponse<ApiData<CasinoStatistics[]>>>(endpoint, {
      params,
    });
  }

  public getReportByBets(filter: object): Observable<ApiResponse<ApiData<any[]>>> {
    const params = new HttpParams({ fromObject: { ...filter } });
    const endpoint = `${this.apiReportsUrl}/casino/report-by-bets`;
    return this._httpClient.get<ApiResponse<ApiData<any[]>>>(endpoint, {
      params,
    });
  }

  public getReportByPlayers(filter: object): Observable<ApiResponse<ApiData<any[]>>> {
    const params = new HttpParams({ fromObject: { ...filter } });
    const endpoint = `${this.apiReportsUrl}/casino/report-by-players`;
    return this._httpClient.get<ApiResponse<ApiData<any[]>>>(endpoint, {
      params,
    });
  }

  public getReportByProvider(filter: object): Observable<ApiResponse<ProviderReportData>> {
    const params = new HttpParams({ fromObject: { ...filter } });
    const endpoint = `${this.apiReportsUrl}/casino/report-by-provider`;
    return this._httpClient.get<ApiResponse<ProviderReportData>>(endpoint, {
      params,
    });
  }

  public getBackOfficeSessionHistoryReport(
    filter: object
  ): Observable<ApiResponse<ApiData<LoginAttemptBackOffice[]>>> {
    const params = new HttpParams({ fromObject: { ...filter } });
    const endpoint = `${this.apiReportsUrl}/login-histories/backoffice`;
    return this._httpClient.get<ApiResponse<ApiData<LoginAttemptBackOffice[]>>>(endpoint, {
      params,
    });
  }

  public getBackOfficeSessionHistoryGrouped(
    filter: object
  ): Observable<ApiResponse<ApiData<GroupedLoginAttemptBackOffice[]>>> {
    const params = new HttpParams({ fromObject: { ...filter } });
    const endpoint = `${this.apiReportsUrl}/login-histories/backoffice`;
    return this._httpClient.get<ApiResponse<ApiData<GroupedLoginAttemptBackOffice[]>>>(
      endpoint,
      {
        params,
      }
    );
  }

  public getPlayerSessionHistoryReport(
    filter: object
  ): Observable<ApiResponse<ApiData<LoginAttemptPlayer[]>>> {
    const params = new HttpParams({ fromObject: { ...filter } });
    const endpoint = `${this.apiReportsUrl}/login-histories/player`;
    return this._httpClient.get<ApiResponse<ApiData<LoginAttemptPlayer[]>>>(endpoint, {
      params,
    });
  }

  public getPlayerSessionHistoryGrouped(
    filter: object
  ): Observable<ApiResponse<ApiData<GroupedLoginAttemptBackOffice[]>>> {
    const params = new HttpParams({ fromObject: { ...filter } });
    const endpoint = `${this.apiReportsUrl}/login-histories/player`;
    return this._httpClient.get<ApiResponse<ApiData<GroupedLoginAttemptBackOffice[]>>>(
      endpoint,
      {
        params,
      }
    );
  }

  getAllGeneralProductsReport(
    filter: object
  ): Observable<ApiResponse<ApiData<BettingCategoryStats[]>>> {
    const endpoint = `${this.apiReportsUrl}/general-products`;
    const params = new HttpParams({ fromObject: { ...filter } });
    return this._httpClient.get<ApiResponse<ApiData<BettingCategoryStats[]>>>(endpoint, {
      params,
    });
  }

  getAllAgentStats(filter: object): Observable<ApiResponse<ApiData<BettingCategoryStats[]>>> {
    const endpoint = `${this.apiReportsUrl}/agent-stats`;
    const params = new HttpParams({ fromObject: { ...filter } });
    return this._httpClient.get<ApiResponse<ApiData<BettingCategoryStats[]>>>(endpoint, {
      params,
    });
  }

  getAllAgentReportsDatamart(
    filter: object
  ): Observable<ApiResponse<ApiData<BettingCategoryStats[]>>> {
    const endpoint = `${this.apiDatamartUrl}/agent-reports-datamart`;
    const params = new HttpParams({ fromObject: { ...filter } });
    return this._httpClient.get<ApiResponse<ApiData<BettingCategoryStats[]>>>(endpoint, {
      params,
    });
  }

  getAllPlayerReportsDatamart(
    filter: object
  ): Observable<ApiResponse<ApiData<BettingCategoryStats[]>>> {
    const endpoint = `${this.apiDatamartUrl}/player-reports-datamart`;
    const params = new HttpParams({ fromObject: { ...filter } });
    return this._httpClient.get<ApiResponse<ApiData<BettingCategoryStats[]>>>(endpoint, {
      params,
    });
  }

  getAllPlayerStats(filter: object): Observable<ApiResponse<ApiData<BettingCategoryStats[]>>> {
    const endpoint = `${this.apiReportsUrl}/player-stats`;
    const params = new HttpParams({ fromObject: { ...filter } });
    return this._httpClient.get<ApiResponse<ApiData<BettingCategoryStats[]>>>(endpoint, {
      params,
    });
  }

  public getBonusPromotionalCodeReport(
    filter: object
  ): Observable<ApiResponse<ApiData<BonusPromotionalCode[]>>> {
    const params = new HttpParams({ fromObject: { ...filter } });
    const endpoint = `${this.apiReportsUrl}/bonus`;
    return this._httpClient.get<ApiResponse<ApiData<BonusPromotionalCode[]>>>(endpoint, {
      params,
    });
  }

  public getReportLiveCasinoByBets(
    filter: object
  ): Observable<ApiResponse<ApiData<CasinoBetReport[]>>> {
    const params = new HttpParams({ fromObject: { ...filter } });
    const endpoint = `${this.apiReportsUrl}/live-casino/report-by-bets`;
    return this._httpClient.get<ApiResponse<ApiData<CasinoBetReport[]>>>(endpoint, {
      params,
    });
  }

  public getReportByLiveCasimoGames(filter: object): Observable<ApiResponse<ApiData<any[]>>> {
    const endpoint = `${this.apiReportsUrl}/live-casino/report-by-games`;
    const params = new HttpParams({ fromObject: { ...filter } });
    return this._httpClient.get<ApiResponse<ApiData<any[]>>>(endpoint, {
      params,
    });
  }

  public getReportByLiveCasinoCurrency(
    filter: object
  ): Observable<ApiResponse<ApiData<CasinoStatistics[]>>> {
    const params = new HttpParams({ fromObject: { ...filter } });
    const endpoint = `${this.apiReportsUrl}/live-casino/report-by-currencies`;
    return this._httpClient.get<ApiResponse<ApiData<CasinoStatistics[]>>>(endpoint, {
      params,
    });
  }

  public getReportByLiveCasinoPlayers(
    filter: object
  ): Observable<ApiResponse<ApiData<PlayerReport[]>>> {
    const params = new HttpParams({ fromObject: { ...filter } });
    const endpoint = `${this.apiReportsUrl}/live-casino/report-by-players`;
    return this._httpClient.get<ApiResponse<ApiData<PlayerReport[]>>>(endpoint, {
      params,
    });
  }

  public getReportByLiveCasinoProviders(
    filter: object
  ): Observable<ApiResponse<ProviderReportData>> {
    const params = new HttpParams({ fromObject: { ...filter } });
    const endpoint = `${this.apiReportsUrl}/live-casino/report-by-provider`;
    return this._httpClient.get<ApiResponse<ProviderReportData>>(endpoint, {
      params,
    });
  }
  public getCasinoReportForGeneralAgents(
    product: string | null,
    filter: object
  ): Observable<ApiResponse<ApiData<GeneralCasionoReportByAgent[]>>> {
    const params = new HttpParams({ fromObject: { ...filter } });
    const endpoint = `${this.apiUrl}/general-products/${product}`;
    return this._httpClient.get<ApiResponse<ApiData<GeneralCasionoReportByAgent[]>>>(endpoint, {
      params,
    });
  }

  // Served by the reports backend (same DB and JWT) so heavy exports don't block
  // the main API.
  public downloadReport(request: DownloadReportRequest): Observable<HttpResponse<Blob>> {
    const endpoint = `${this.apiReportsUrl}/download`;
    const language = this._translate.currentLang;
    const headers = new HttpHeaders({
      'Accept-Language': language,
    });
    return this._httpClient.post(endpoint, request, {
      headers,
      params: new HttpParams().set('format', 'file'),
      responseType: 'blob',
      observe: 'response',
    });
  }

  public getReportHistory(filter: object): Observable<ApiResponse<ApiData<ReportHistoryItem[]>>> {
    const params = new HttpParams({ fromObject: { ...filter } });
    const endpoint = `${this.apiUrl}/get-all-history`;
    return this._httpClient.get<ApiResponse<ApiData<ReportHistoryItem[]>>>(endpoint, { params });
  }

  public getAllVirtualBets(filter: object): Observable<ApiResponse<ApiData<VirtualBet[]>>> {
    const params = new HttpParams({ fromObject: { ...filter } });
    const endpoint = `${this.apiReportsUrl}/virtual-bets`;
    return this._httpClient.get<ApiResponse<ApiData<VirtualBet[]>>>(endpoint, {
      params,
    });
  }
}
